import * as THREE from "three";
import { wrapAngle, getActiveAttachmentPoints } from "../utils/geometry.js";

/**
 * AttachmentPoint class
 * Represents a point where furniture can be attached to other furniture
 */
/**
 * Anchor types mirror the Unity designer's AnchorType enum.
 * Left↔Right and Middle↔Middle are the valid pairing rules.
 * When either type is null, any type matches (backward compat).
 */
const ANCHOR_TYPE_MATCHES = {
  left:  'right',
  right: 'left',
  middle: 'middle',
};

const DEG2RAD = Math.PI / 180;

// Outward-normal angle (radians, measured atan2(normalX, normalZ)) for each
// cardinal side. Used as the default when an AP has no explicit facingAngle.
const SIDE_NORMAL_ANGLE = {
  front:  0,
  right:  Math.PI / 2,
  back:   Math.PI,
  left:  -Math.PI / 2,
};

// A component's rest yaw relative to its face's outward normal; every rest yaw, slide and flank axis derives from it.
export const COMPONENT_FACING = Math.PI;
const _componentTurn = COMPONENT_FACING - Math.PI / 2;
// A component's long axis and across-the-face axis, in its own frame.
export const COMPONENT_LENGTH_AXIS = Object.freeze(new THREE.Vector3(0, 0, 1).applyAxisAngle(new THREE.Vector3(0, 1, 0), _componentTurn));
export const COMPONENT_ACROSS_AXIS = Object.freeze(new THREE.Vector3(1, 0, 0).applyAxisAngle(new THREE.Vector3(0, 1, 0), _componentTurn));

// Plus size in world metres, never rescaled for the camera.
export const MARKER_SIZE = 0.18;
const DEBUG_DOT_SIZE = 0.12;
const MARKER_TEXTURE_SIZE = 128;
const MARKER_COLOR = '#4facfe';
const MARKER_COLOR_VALID = '#00ff88';
const MARKER_COLOR_INVALID = '#ff3333';

// Every plus in a state looks the same, and they are all on screen at once: one texture per state.
const _markerTextures = new Map();

function markerTexture(color) {
  let texture = _markerTextures.get(color);
  if (!texture) {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = MARKER_TEXTURE_SIZE;
    drawPlus(canvas.getContext('2d'), MARKER_TEXTURE_SIZE, color);
    texture = new THREE.CanvasTexture(canvas);
    _markerTextures.set(color, texture);
  }
  return texture;
}

// A ring with a faint fill and a "+" in the state's colour.
function drawPlus(ctx, size, color) {
  const center = size / 2;
  const outerR = size * 0.42;
  const ringW = size * 0.06;
  ctx.clearRect(0, 0, size, size);

  ctx.fillStyle = color + '22';
  ctx.beginPath();
  ctx.arc(center, center, outerR, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = color;
  ctx.lineWidth = ringW;
  ctx.beginPath();
  ctx.arc(center, center, outerR - ringW / 2, 0, Math.PI * 2);
  ctx.stroke();

  const arm = outerR * 0.42;
  const thick = Math.max(3, size * 0.08);
  ctx.fillStyle = color;
  ctx.fillRect(center - arm, center - thick / 2, arm * 2, thick);
  ctx.fillRect(center - thick / 2, center - arm, thick, arm * 2);
}

// Pre-allocated scratch objects for allocation-free hot-path methods
const _scratchWorldPos = new THREE.Vector3();
const _scratchParentQuat = new THREE.Quaternion();
const _scratchLocalQuat = new THREE.Quaternion();
const _scratchFinalQuat = new THREE.Quaternion();
const _Y_AXIS = new THREE.Vector3(0, 1, 0);
const _scratchOffsetVec = new THREE.Vector3();

export class AttachmentPoint {
  /**
   * @param {*} parentFurniture
   * @param {string} side
   * @param {THREE.Vector3} position
   * @param {string[]} acceptsComponentTypes
   * @param {string|null} anchorType - "left", "middle", or "right" (null = any)
   * @param {number} compatibilityTag - 0 = universal, non-zero must match (asymmetric: checked from child/local side)
   * @param {number|null} facingAngle - Optional outward-normal angle in DEGREES
   *   (atan2(normalX, normalZ), i.e. 0 = +Z/front, 90 = +X/right). When set it
   *   overrides the cardinal `side` normal, allowing attachment faces at any
   *   angle (e.g. the curved edges of matacao3 corner modules). `side` is still
   *   kept as the nearest-cardinal label for adjacency / visibility logic.
   */
  constructor(parentFurniture, side, position, acceptsComponentTypes = [], anchorType = null, compatibilityTag = 0, facingAngle = null) {
    this.parentFurniture = parentFurniture;
    this.side = side;
    this.acceptsComponentTypes = acceptsComponentTypes;
    this.anchorType = anchorType;
    this.compatibilityTag = compatibilityTag;
    this.facingAngle = facingAngle ?? null;
    this.isOccupied = false;
    this.attachedFurniture = null;
    // Set by setRange(): a span components seat anywhere along, several at once.
    this.range = null;
    this.rangeAxis = null;
    this.occupants = null;
    this.runs = null;

    // Store LOCAL position and rotation (relative to parent furniture)
    this.originalLocalPosition = position.clone(); // Store original for resetting
    this.localPosition = position.clone();
    // localRotationY orients the marker / attached-component offset. Derive it
    // from the outward-normal angle so explicit facingAngle APs orient correctly.
    this.localRotationY = AttachmentPoint.baseRotationY(side, this.facingAngle);

    // Create visual marker (purely for display)
    this.createMarker();
  }

  /**
   * Check if two anchor types can connect (Unity-style pairing).
   * Left↔Right, Middle↔Middle. Null = wildcard (matches anything).
   */
  static typesCanMatch(typeA, typeB) {
    if (!typeA || !typeB) return true; // null = any
    return ANCHOR_TYPE_MATCHES[typeA] === typeB;
  }

  /**
   * Outward-normal angle (radians) of an attachment face.
   * Prefers an explicit `facingAngle` (degrees) when provided, otherwise falls
   * back to the cardinal `side`. Works on both AttachmentPoint instances and
   * raw furniture.json AP config objects, so SnapSystem can use it for parent
   * (instance) and child (config) alike.
   * Convention: angle = atan2(normalX, normalZ) → 0 = +Z (front), 90° = +X (right).
   */
  static normalAngle(side, facingAngle = null) {
    if (facingAngle !== null && facingAngle !== undefined) return facingAngle * DEG2RAD;
    return SIDE_NORMAL_ANGLE[side] ?? 0;
  }

  /**
   * Stable grouping key for "which face is this AP on". APs with the same
   * faceKey form one connecting face (their span is measured for overlap).
   * facingAngle APs key off the angle so each curved-edge anchor is its own
   * face; cardinal APs key off the side string (unchanged behaviour).
   */
  static faceKey(side, facingAngle = null) {
    return (facingAngle !== null && facingAngle !== undefined) ? `a${facingAngle}` : side;
  }

  /**
   * Check if two attachment points can connect, considering both
   * anchor type pairing and compatibility tags.
   *
   * Asymmetric Unity-parity rule (FurnitureManager.cs:1142): the local/dragged
   * tag of 0 means "accepts anything"; otherwise both tags must equal.
   * Tag 0 (local) ↔ tag 0 (other) = universal.
   * Tag 0 (local) ↔ tag N (other) = allowed (universal child snaps to restricted parent).
   * Tag N (local) ↔ tag 0 (other) = blocked (restricted child rejects universal parent).
   * Tag N (local) ↔ tag N (other) = allowed (restricted pairing, e.g. bard R↔R short sides).
   *
   * Bad fits this rule alone would permit are rejected downstream by collision
   * gating (EventManager checkFurnitureCollisions + checkSnapPenetration),
   * mirroring Unity's CanFitInPosition step.
   */
  static canMatch(localAP, otherAP) {
    if (!AttachmentPoint.typesCanMatch(localAP.anchorType, otherAP.anchorType)) return false;
    const localTag = localAP.compatibilityTag ?? 0;
    const otherTag = otherAP.compatibilityTag ?? 0;
    if (localTag !== 0 && localTag !== otherTag) return false;
    return true;
  }

  /** Whether an AP's `accepts` / `acceptsOnly` lists admit a child of this type and urlCode. */
  static admits(accepts, acceptsOnly, componentType, urlCode) {
    return (accepts ?? []).includes(componentType) && (!acceptsOnly?.length || acceptsOnly.includes(urlCode));
  }

  /**
   * Whether `host` may carry the dragged component at all: panels go on modules only, and an
   * unrestricted slot for its own type would let a component stack endlessly on that type.
   */
  static hostTakes(host, furnitureData) {
    const type = furnitureData?.componentType;
    if (type === 'panel' && host.furnitureData?.componentType !== 'module') return false;
    if (!type || type === 'module' || host.furnitureData?.componentType !== type) return true;
    return !getActiveAttachmentPoints(furnitureData).some(ap =>
      ap.accepts?.includes(type) && !ap.acceptsOnly?.length && !ap.requiresParent?.length);
  }

  /** Unity's CushionRanges: any number seat along `[min, max]` (m); never occupied, free runs are computed. */
  setRange([min, max]) {
    this.range = { min, max };
    this.rangeAxis = AttachmentPoint.slideAxis(this);
    this.occupants = new Map();
  }

  /** Parent-local unit axis a component slides along on this AP, its dominant component positive. */
  static slideAxis(ap) {
    const axis = new THREE.Vector3();
    if (ap.facingAngle != null) {
      const theta = ap.facingAngle * DEG2RAD;
      axis.set(Math.cos(theta), 0, -Math.sin(theta));
    } else if (ap.side === 'front' || ap.side === 'back') {
      axis.set(1, 0, 0);
    } else if (ap.side === 'left' || ap.side === 'right') {
      axis.set(0, 0, 1);
    } else {
      axis.copy(COMPONENT_LENGTH_AXIS).applyAxisAngle(_Y_AXIS, ap.componentRotationY ?? ap.localRotationY);
    }
    const dominant = Math.abs(axis.x) >= Math.abs(axis.z) ? axis.x : axis.z;
    return dominant < 0 ? axis.negate() : axis;
  }

  rangePoint(t, target) {
    return target.copy(this.originalLocalPosition).addScaledVector(this.rangeAxis, t);
  }

  /** Range parameter of a parent-local point, projected onto the range axis. */
  rangeParam(localPoint) {
    return _scratchOffsetVec.copy(localPoint).sub(this.originalLocalPosition).dot(this.rangeAxis);
  }

  /** Where `furniture` sits on this AP, parent-local: its own slide on a ranged AP, else the AP's. */
  slideOf(furniture) {
    return this.occupants?.get(furniture) ?? this.localPosition;
  }

  setSlide(furniture, v) {
    const target = this.occupants ? this.occupants.get(furniture) : this.localPosition;
    target?.set(v.x, v.y, v.z);
  }

  holds(furniture) {
    return this.occupants ? this.occupants.has(furniture) : this.isOccupied && this.attachedFurniture === furniture;
  }

  /** One plus per free run's middle; mids closer than a plus merge, as Unity merges within 20 cm. */
  setRuns(runs) {
    if (runs === this.runs) return;
    this.runs = runs;
    const mids = (this._runMids ??= []);
    mids.length = 0;
    for (const run of runs ?? []) {
      if (mids.length && run.mid - mids[mids.length - 1] < MARKER_SIZE) continue;
      mids.push(run.mid);
    }
    const sprites = (this._runSprites ??= []);
    while (sprites.length < mids.length - 1) {
      const sprite = this._newMarkerSprite();
      this.group.add(sprite);
      sprites.push(sprite);
    }
    sprites.forEach((sprite, i) => { sprite.visible = i < mids.length - 1; });
    this._refreshMarkerColors();
    this.update();
  }

  /** Whether the dragged component can seat on this AP: the snap's rule, before occupancy and placement rules. */
  seats(furnitureData) {
    return AttachmentPoint.hostTakes(this.parentFurniture, furnitureData) &&
      AttachmentPoint.admits(this.acceptsComponentTypes, this.acceptsOnly, furnitureData?.componentType, furnitureData?.urlCode);
  }

  /** Marker / component-offset orientation of a face, before any authored rotationYOffset. */
  static baseRotationY(side, facingAngle = null) {
    return facingAngle != null
      ? AttachmentPoint.normalAngle(side, facingAngle) + COMPONENT_FACING
      : AttachmentPoint.calculateRotationForSide(side);
  }

  /** The AP frame's local yaw from raw furniture.json data, as localRotationY ends up after rotationYOffset. */
  static frameRotationY(apData) {
    return AttachmentPoint.baseRotationY(apData.side, apData.facingAngle) + (apData.rotationYOffset ?? 0);
  }

  /** Local yaw a component rests at on this AP (raw data): `rotationY` (degrees) wins, else the AP frame's. */
  static restRotationY(apData) {
    if (apData.rotationY !== undefined) return THREE.MathUtils.degToRad(apData.rotationY);
    return AttachmentPoint.frameRotationY(apData);
  }

  static calculateRotationForSide(side) {
    if (side in SIDE_NORMAL_ANGLE) return wrapAngle(SIDE_NORMAL_ANGLE[side] + COMPONENT_FACING);
    if (side === 'top') return COMPONENT_FACING - Math.PI;
    return 0; // inherit: a component seated on a component keeps its parent's frame
  }

  _newMarkerSprite() {
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
      map: markerTexture(MARKER_COLOR), transparent: true, depthTest: false, depthWrite: false,
    }));
    sprite.renderOrder = 999;
    sprite.scale.set(MARKER_SIZE, MARKER_SIZE, 1);
    return sprite;
  }

  createMarker() {
    this.group = new THREE.Group();
    this.sprite = this._newMarkerSprite();
    this.spriteMaterial = this.sprite.material;
    this.group.add(this.sprite);
    this.group.visible = false;

    this.visible = false; // Offered; setDrawn() alone decides what is drawn
    this._debugShown = false;
    this.isHighlighted = false;
  }

  show() {
    if (!this.isOccupied) this.visible = true;
  }

  hide() {
    this.visible = false;
  }

  /** Per frame: draw the marker while its piece is drawn, if it is a debug dot or an offered plus `wanted` now. */
  setDrawn(pieceShown, wanted) {
    const room = !this.range || this.runs?.length !== 0;
    this.group.visible = pieceShown && (this._debugShown || (this.visible && wanted && room));
  }

  drawDebugDot(ctx, size, color, label) {
    const center = size / 2;
    const radius = size * 0.35;
    ctx.clearRect(0, 0, size, size);

    // Filled circle
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(center, center, radius, 0, Math.PI * 2);
    ctx.fill();

    // Side letter label
    if (label) {
      ctx.fillStyle = '#000000';
      ctx.font = `bold ${Math.round(size * 0.38)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, center, center);
    }
  }

  /** Debug overlay (D): show this AP as a coloured, labelled dot whatever its state. */
  showDebug(color) {
    // The plus textures are shared, so a labelled dot draws into a canvas of its own.
    if (!this._debugTexture) {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = MARKER_TEXTURE_SIZE;
      this._debugTexture = new THREE.CanvasTexture(canvas);
    }
    const canvas = this._debugTexture.image;
    const label = { front: 'F', back: 'B', left: 'L', right: 'R', top: 'T' }[this.side] ?? this.side[0].toUpperCase();
    this.drawDebugDot(canvas.getContext('2d'), canvas.width, color, label);
    this._debugTexture.needsUpdate = true;
    this.spriteMaterial.map = this._debugTexture;
    this.sprite.scale.set(DEBUG_DOT_SIZE, DEBUG_DOT_SIZE, 1);
    this._debugShown = true;
    this.setRuns(null);
    // Placed even when not offered, since update() skips those.
    this.getWorldPosition(this.group.position);
  }

  /** Restore the normal sprite appearance after a debug session. */
  hideDebug() {
    this._debugShown = false;
    this.spriteMaterial.map = markerTexture(this._markerColor());
    this.sprite.scale.set(MARKER_SIZE, MARKER_SIZE, 1);
  }

  setOccupied(furniture) {
    if (this.occupants) {
      this.occupants.set(furniture, this.originalLocalPosition.clone());
      return;
    }
    this.isOccupied = true;
    this.attachedFurniture = furniture;
    this.hide();
  }

  setUnoccupied(furniture) {
    if (this.occupants) {
      this.occupants.delete(furniture);
      return;
    }
    this.isOccupied = false;
    this.attachedFurniture = null;
    // Reset position to original default position
    this.localPosition.copy(this.originalLocalPosition);
    // Don't automatically show - let parent furniture decide visibility
    // based on shouldShowAttachmentPoint() logic
  }

  /**
   * Calculate world position mathematically from parent's transform
   * NEVER uses the marker's position from the scene graph
   */
  /**
   * @param {THREE.Vector3} [target] - Optional target vector to write into (avoids allocation)
   */
  getWorldPosition(target) {
    const parentMesh = this.parentFurniture.getMesh();
    // Ancestors + self is all matrixWorld depends on; the descendants needn't be refreshed.
    parentMesh.updateWorldMatrix(true, false);

    const out = target || new THREE.Vector3();
    out.copy(this.localPosition);
    out.applyMatrix4(parentMesh.matrixWorld);
    return out;
  }

  /**
   * @param {THREE.Quaternion} [target] - Optional target quaternion to write into (avoids allocation)
   */
  getAttachmentOrientation(target) {
    const parentMesh = this.parentFurniture.getMesh();
    parentMesh.getWorldQuaternion(_scratchParentQuat);
    _scratchLocalQuat.setFromAxisAngle(_Y_AXIS, this.localRotationY);

    const out = target || new THREE.Quaternion();
    out.multiplyQuaternions(_scratchParentQuat, _scratchLocalQuat);
    return out;
  }

  /**
   * Resolve the placement offset for a component seating on this AP. A per-AP,
   * per-urlCode override (connectionOffsetByUrlCode) takes precedence over the
   * component's own properties.connectionOffset, so mirrored or angled slots can
   * place the same pillow at slot-correct offsets — the cushion-level offset is a
   * single shared value that can't differ between slots.
   * @param {object|null} furnitureData
   * @returns {{x:number,y:number,z:number}|null}
   */
  resolveConnectionOffset(furnitureData) {
    return this.connectionOffsetByUrlCode?.[furnitureData?.urlCode]
      ?? furnitureData?.properties?.connectionOffset
      ?? null;
  }

  /**
   * Get world position with connectionOffset applied (rotated by AP orientation).
   * Uses module-level scratch objects — allocation-free when target is provided.
   * @param {object|null} connectionOffset - {x, y, z} offset or null
   * @param {THREE.Vector3} [target] - Optional target vector
   */
  getWorldPositionWithOffset(connectionOffset, target) {
    const pos = this.getWorldPosition(target);
    if (connectionOffset) {
      const orient = this.getAttachmentOrientation(_scratchFinalQuat);
      _scratchOffsetVec.set(connectionOffset.x, connectionOffset.y, connectionOffset.z);
      _scratchOffsetVec.applyQuaternion(orient);
      pos.add(_scratchOffsetVec);
    }
    return pos;
  }

  /** Place the marker at this AP; `parentMatrixWorld` saves recomputing the piece's world matrix. */
  update(parentMatrixWorld = null) {
    if (!parentMatrixWorld) {
      const parentMesh = this.parentFurniture.getMesh();
      parentMesh.updateWorldMatrix(true, false);
      parentMatrixWorld = parentMesh.matrixWorld;
    }
    const mids = this.range ? this._runMids : null;
    if (!mids?.length) {
      this.group.position.copy(this.localPosition).applyMatrix4(parentMatrixWorld);
      this.sprite.position.set(0, 0, 0);
      return;
    }
    // The group sits at the AP; each run's plus is offset from it in world axes.
    this.group.position.copy(this.originalLocalPosition).applyMatrix4(parentMatrixWorld);
    for (let i = 0; i < mids.length; i++) {
      const sprite = i === 0 ? this.sprite : this._runSprites[i - 1];
      this.rangePoint(mids[i], sprite.position).applyMatrix4(parentMatrixWorld).sub(this.group.position);
    }
  }

  dispose() {
    this.group.parent?.remove(this.group);
    // The plus textures and the sprite geometry are shared by every marker.
    this.spriteMaterial.dispose();
    for (const sprite of this._runSprites ?? []) sprite.material.dispose();
    this._debugTexture?.dispose();
  }

  getMesh() {
    return this.group;
  }

  /**
   * Highlight this attachment point (when module is hovering near during drag)
   */
  setHighlight(highlighted, isValid = true) {
    // Don't highlight if occupied
    if (highlighted && this.isOccupied) return;

    if (this.isHighlighted === highlighted && this._highlightValid === isValid) return;

    this.isHighlighted = highlighted;
    this._highlightValid = isValid;
    this._refreshMarkerColors();
  }

  /** On a ranged AP only the plus nearest where the dragged piece slides lights up. */
  setActiveRunParam(t) {
    if (this._activeRunParam === t) return;
    this._activeRunParam = t;
    this._refreshMarkerColors();
  }

  _refreshMarkerColors() {
    const mids = this.range ? this._runMids : null;
    if (!mids?.length) {
      if (!this._debugShown) this.spriteMaterial.map = markerTexture(this._markerColor());
      return;
    }
    const t = this._activeRunParam;
    let active = -1;
    if (t != null) {
      let best = Infinity;
      mids.forEach((mid, i) => {
        const d = Math.abs(mid - t);
        if (d < best) { best = d; active = i; }
      });
    }
    for (let i = 0; i < mids.length; i++) {
      if (i === 0 && this._debugShown) continue;
      const material = i === 0 ? this.spriteMaterial : this._runSprites[i - 1].material;
      material.map = markerTexture(i === active ? this._markerColor() : MARKER_COLOR);
    }
  }

  _markerColor() {
    if (!this.isHighlighted) return MARKER_COLOR;
    return this._highlightValid ? MARKER_COLOR_VALID : MARKER_COLOR_INVALID;
  }
}
